
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Order, User as AppUser, StoreSettings } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateNumericReferralCode(): string {
  let code = '';
  for (let i = 0; i < 9; i++) {
    code += Math.floor(Math.random() * 10);
  }
  return code;
}

export function validateCPF(cpf: string): boolean {
  cpf = cpf.replace(/[^\d]+/g, '');

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== parseInt(cpf.substring(9, 10))) {
    return false;
  }

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== parseInt(cpf.substring(10, 11))) {
    return false;
  }

  return true;
}

export function createSlug(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');
}

export interface RankInfo {
  name: string;
  bgClass: string;
}

export const getRankInfo = (orderCount: number): RankInfo | null => {
  if (orderCount >= 1 && orderCount <= 10) return { name: 'Bronze', bgClass: 'bg-rank-bronze' };
  if (orderCount >= 11 && orderCount <= 20) return { name: 'Silver', bgClass: 'bg-rank-silver' };
  if (orderCount >= 21 && orderCount <= 30) return { name: 'Gold', bgClass: 'bg-rank-gold' };
  if (orderCount >= 31 && orderCount <= 40) return { name: 'Diamond', bgClass: 'bg-rank-diamond' };
  if (orderCount > 40) return { name: 'Obsidian', bgClass: 'bg-rank-obsidian' };
  return null;
};

export const calculateDriverEarning = (order: Order | undefined | null): number => {
    if (!order) return 0;
    if (order.driverEarning !== undefined) return order.driverEarning;
    const fee = order.deliveryFee;
    if (fee === 0) return 12;
    if (fee === 17 || fee === 20) return 27;
    if (fee === 40) return 40;
    return fee;
};

export const determineCollectionName = (order: Order | Partial<Order>): 'orders' | 'FastDelivery' | 'OrdersExpress' => {
    if ((order as any)._collection) return (order as any)._collection;
    const dt = order.deliveryTime || '';
    if (dt === "Envio Imediato" || dt === "Prioridade") return 'FastDelivery';
    if (dt.includes("Wolf Express") || dt.includes("Express") || (order.products && order.products.some(p => !!p.addedFrom))) {
        return 'OrdersExpress';
    }
    return 'orders';
};

export const replaceOrderVariables = (template: string, orderData: Order, appUser: AppUser | null) => {
  if (!template) return "";

  const paymentMethodMap: { [key: string]: string } = {
    'pix': 'PIX (Online)',
    'card-delivery': 'Cartão na Entrega',
    'cash-delivery': 'Dinheiro na Entrega',
    'pix-delivery': 'PIX na Entrega',
  };

  const productsList = (orderData.products || []).map(p => {
    const variant = p.variantId ? p.variants?.find(v => v && v.id === p.variantId) : null;
    const variantName = variant?.options?.name;
    const displayName = (variantName && !p.name.includes(variantName)) 
      ? `*${p.name} (${variantName})*` 
      : `*${p.name}*`;
    
    return `   - ${p.quantity}x ${displayName} (R$ ${p.price.toFixed(2)})`;
  }).join('\n');
  
  const paymentMethodText = paymentMethodMap[orderData.payment?.method] || orderData.payment?.method || 'N/A';
  
  let changeText = '';
  if (orderData.payment?.method === 'cash-delivery' && orderData.payment?.cashAmount) {
    changeText = ` (Pagar com R$ ${orderData.payment.cashAmount.toFixed(2)}`;
    if (orderData.payment.change && orderData.payment.change > 0) {
      changeText += `, Troco R$ ${orderData.payment.change.toFixed(2)}`;
    } else {
      changeText += `, Sem Troco`;
    }
    changeText += `)`;
  }

  const subtotalValue = orderData.subtotal || orderData.total || 0;
  const deliveryValue = orderData.deliveryFee || 0;
  const discountValue = orderData.discount || 0;
  const debtValue = orderData.debtPaid?.amount || 0;
  const totalValue = orderData.total || 0;

  let result = template;
  const replacements: { [key: string]: string } = {
    '{ID_PEDIDO}': `#${(orderData.id || '').slice(0, 6).toUpperCase()}`,
    '{NOME_CLIENTE}': orderData.customerName || 'N/A',
    '{CLIENTE}': orderData.customerName || 'N/A',
    '{CONTATO_CLIENTE}': (orderData.customerContact || '').replace(/\D/g, ""),
    '{ENDERECO}': `${orderData.deliveryAddress || ''}, ${orderData.deliveryCity || ''}`,
    '{AGENDAMENTO}': orderData.deliveryTime || 'N/A',
    '{LISTA_PRODUTOS}': productsList,
    '{SUBTOTAL}': subtotalValue.toFixed(2),
    '{TAXA_ENTREGA}': deliveryValue.toFixed(2),
    '{DESCONTO}': discountValue.toFixed(2),
    '{DEBITO_PENDENTE}': debtValue.toFixed(2),
    '{TOTAL}': totalValue.toFixed(2),
    '{METODO_PAGAMENTO}': `${paymentMethodText}${changeText}`,
    '{OBSERVACOES}': orderData.notes || 'Nenhuma'
  };

  Object.entries(replacements).forEach(([key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special chars for regex
    const regex = new RegExp(escapedKey, 'g');
    result = result.replace(regex, value);
  });

  return result;
};

export const formatOrderForWhatsApp = (orderData: Order, appUser: AppUser | null, settings: StoreSettings | null) => {
    const getMessageTemplate = () => {
        if (orderData.deliveryTime === "Envio Imediato" && settings?.fastDeliveryConfirmationMessage) {
            return settings.fastDeliveryConfirmationMessage;
        }
        if (orderData.isPriority && settings?.wolfExpressConfirmationMessage) {
            return settings.wolfExpressConfirmationMessage;
        }
        if (settings?.orderConfirmationMessage) {
            return settings.orderConfirmationMessage;
        }
        const defaultTitle = orderData.isPriority ? '*Novo Pedido PRIORIDADE!* 🚀' : '*Novo Pedido Recebido!* 🚀';
        return `${defaultTitle}\n\n*Pedido:* {ID_PEDIDO}\n*Cliente:* {NOME_CLIENTE}\n*Contato:* {CONTATO_CLIENTE}\n\n*Endereço:* {ENDERECO}\n*Agendamento:* {AGENDAMENTO}\n\n---\n*Resumo do Pedido:*\n{LISTA_PRODUTOS}\n---\n\n*Subtotal:* R$ {SUBTOTAL}\n{TAXA_ENTREGA}\n{DESCONTO}\n{DEBITO_PENDENTE}\n*Total:* *R$ {TOTAL}*\n\n*Pagamento:* {METODO_PAGAMENTO}\n\n*Observações:* {OBSERVACOES}`;
    };

    return encodeURIComponent(replaceOrderVariables(getMessageTemplate(), orderData, appUser));
};
